"use client";
import { NavigationTabs } from "@/components/common/navigationTabs";
import { TableContainer } from "@/components/common/TableContainer";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useCallback, useEffect, useState } from "react";
import { columns } from "@/components/tickets/columns";
import { useNavigate, useParams } from "react-router-dom";
import { DataView } from "@/components/common/DataView";
import { ActionButton } from "@/components/common/DataViewTypes";
import { Plus, RefreshCw } from "lucide-react";
import { Ticket, TicketStatus, TicketFilters } from "@/types/models/ticket";
import {
  fetchTicketsRequest,
  searchTicketsRequest,
  clearSearchResults,
} from "@/store/slices/ticket.slice";

// Map UI tabs to API statuses
const ticketTabs = [
  { label: "Open", status: "open" as TicketStatus },
  { label: "In Progress", status: "in_progress" as TicketStatus },
  { label: "Pending Customer", status: "pending_customer" as TicketStatus },
  { label: "Resolved", status: "resolved" as TicketStatus },
  { label: "Closed", status: "closed" as TicketStatus },
  { label: "All", status: null },
];

const Tickets = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<TicketStatus | null>("open");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters] = useState<TicketFilters>({});
  const pageSize = 10;

  const {
    items,
    pagination,
    loading,
    error,
    searchResults,
    searchPagination,
    searchLoading,
    loaded,
  } = useAppSelector((state) => state.tickets);

  const fetchTickets = useCallback(() => {
    const hasFilters = Object.keys(appliedFilters).length > 0;
    const hasSearch = searchQuery.trim();

    if (hasSearch || hasFilters) {
      // Build search/filter parameters
      const searchParams: any = {
        q: searchQuery,
        page: pageIndex + 1,
        limit: pageSize,
        filters: {
          ...appliedFilters,
        },
      };

      // If we have an active tab and no status filter, use tab status
      if (activeTab && !appliedFilters.status) {
        searchParams.filters.status = activeTab;
      }

      dispatch(searchTicketsRequest(searchParams));
    } else {
      dispatch(
        fetchTicketsRequest({
          page: pageIndex + 1,
          limit: pageSize,
          sortBy: "createdAt",
          sortOrder: "desc",
          filters: {
            ...(activeTab && { status: activeTab }),
            ...appliedFilters,
          },
        }),
      );
    }
  }, [appliedFilters, searchQuery, pageIndex, activeTab, dispatch]);

  // Use search results when searching or filtering, otherwise use regular list
  const hasSearchOrFilters =
    searchQuery.trim() || Object.keys(appliedFilters).length > 0;
  const currentData = hasSearchOrFilters ? searchResults || [] : items || [];
  const currentPagination = hasSearchOrFilters ? searchPagination : pagination;
  const currentLoading = hasSearchOrFilters ? searchLoading : loading;

  useEffect(() => {
    // Only fetch if tickets have never been fetched in this session
    // Allow pagination fetches when pageIndex > 0
    if (!loaded) {
      // initial load or tab change when not yet fetched
      if (currentData.length === 0 || pageIndex > 0) {
        fetchTickets();
      }
    } else if (pageIndex > 0) {
      // tickets were fetched previously but user is paginating — fetch specific page
      fetchTickets();
    }
  }, [dispatch, pageIndex, activeTab, currentData.length, loaded]);

  useEffect(() => {
    setPageIndex(0); // Reset to first page when search or filters change
  }, [searchQuery, appliedFilters]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
    if (!query.trim()) {
      dispatch(clearSearchResults());
    }
  };

  const handleTabChange = (status: TicketStatus | null) => {
    setActiveTab(status);
    setPageIndex(0);
  };

  const handleRowClick = (ticket: Ticket) => {
    navigate(`/${storeType}/${storeId}/tickets/${ticket.id}`);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const getStatusTitle = () => {
    if (!activeTab) return "All Tickets";
    switch (activeTab) {
      case "open":
        return "Open Tickets";
      case "in_progress":
        return "In Progress Tickets";
      case "pending_customer":
        return "Pending Customer Response";
      case "resolved":
        return "Resolved Tickets";
      case "closed":
        return "Closed Tickets";
      default:
        return "Tickets";
    }
  };

  const getStatusDescription = () => {
    if (!activeTab)
      return "Manage all support tickets and customer communications";
    switch (activeTab) {
      case "open":
        return "New tickets that require attention";
      case "in_progress":
        return "Tickets currently being worked on";
      case "pending_customer":
        return "Tickets waiting for customer response";
      case "resolved":
        return "Tickets that have been resolved";
      case "closed":
        return "Completed and closed tickets";
      default:
        return "Manage your support tickets";
    }
  };

  const handleCreateTicket = () => {
    navigate(`/${storeType}/${storeId}/tickets/create`);
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    const hasFilters = Object.keys(appliedFilters).length > 0;
    const hasSearch = searchQuery.trim();

    if (hasSearch || hasFilters) {
      const searchParams: any = {
        q: searchQuery,
        page: pageIndex + 1,
        limit: pageSize,
        filters: {
          ...appliedFilters,
        },
      };
      if (activeTab && !appliedFilters.status) {
        searchParams.filters.status = activeTab;
      }
      dispatch(searchTicketsRequest(searchParams));
    } else {
      dispatch(
        fetchTicketsRequest({
          page: pageIndex + 1,
          limit: pageSize,
          sortBy: "createdAt",
          sortOrder: "desc",
          filters: {
            ...(activeTab && { status: activeTab }),
            ...appliedFilters,
          },
        }),
      );
    }
  };

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${currentLoading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline",
      disabled: currentLoading,
    },
    {
      label: "Create Ticket",
      icon: <Plus className="h-3 w-3" />,
      onClick: handleCreateTicket,
      variant: "primary",
    },
  ];

  return (
    <div className="space-y-4">
      <TableContainer
        navigationTabs={
          <NavigationTabs
            tabs={ticketTabs.map((tab) => ({
              label: tab.label,
              onClick: () => handleTabChange(tab.status),
              isActive: activeTab === tab.status,
            }))}
            hasBackButton={false}
          />
        }
      >
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={currentData}
            pagination={currentPagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={currentLoading}
            error={error}
            title={getStatusTitle()}
            description={getStatusDescription()}
            searchPlaceholder="Search tickets..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            actionButtons={actionButtons}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="tickets"
            loadingMessage="Loading tickets..."
          />
        </div>
      </TableContainer>
    </div>
  );
};

export default Tickets;
